/*
 * Builds the paste-into-an-LLM report.
 *
 * The point of this file is that "copy the errors" should not mean copying a
 * red badge and a status code. A model asked to fix a broken environment needs
 * the failing thing, the shape of the project around it, and enough context to
 * tell a deploy problem from a config problem: which branch feeds the
 * environment, whether CI passed on it, whether the branch is even promoted
 * yet, and what the failing host said in its own words.
 *
 * Markdown, because that is what every chat surface renders and what every
 * model reads best. Built client-side from data already fetched, so the button
 * is instant and copies exactly what the page is showing.
 */
import type {
  AzureServerSummary,
  AzureSqlDatabaseSummary,
  AzureTableStoreSummary,
  DatabasesResponse,
  EnvironmentHealth,
  OrgHealth,
  RepoHealth,
  RepoState
} from '../types'

const BAD = new Set(['down', 'unreachable', 'error', 'blocked'])

export const isFailing = (e: EnvironmentHealth) => BAD.has(e.status)

/** A one-word verdict for a set of probes, for badges and headings. */
export function rollup(envs: EnvironmentHealth[]): 'ok' | 'degraded' | 'down' | 'unknown' {
  if (!envs.length) return 'unknown'
  const failing = envs.filter(isFailing).length
  if (!failing) return 'ok'
  return failing === envs.length ? 'down' : 'degraded'
}

function fence(body: string, lang = '') {
  // A response body can itself contain a fence; widen ours so it still nests.
  const ticks = body.includes('```') ? '````' : '```'
  return `${ticks}${lang}\n${body}\n${ticks}`
}

function envLines(e: EnvironmentHealth): string[] {
  const out = [
    `- **${e.env}** ${e.url}`,
    `  - result: \`${e.status}\`${e.httpStatus ? ` (HTTP ${e.httpStatus})` : ''}${e.ms !== null ? ` in ${e.ms}ms` : ''}`
  ]
  if (e.error) out.push(`  - error: ${e.error}${e.errorKind ? ` (\`${e.errorKind}\`)` : ''}`)
  if (e.finalUrl) out.push(`  - redirected ${e.redirects}x, ended at ${e.finalUrl}`)
  if (e.body) out.push(`  - response body:`, fence(e.body))
  return out
}

/** The ask, stated once at the top so the model knows what it is looking at. */
function preamble(scope: string) {
  return [
    `# Deployment diagnostics: ${scope}`,
    '',
    'Generated by the F3-Insights repository state dashboard. Everything below is',
    'observed, not inferred: HTTP results come from a server-side probe of the URLs',
    'each repository declares in `.github/branch-viz.json`, and the git state comes',
    'from the GitHub API.',
    '',
    '**What I need:** work out what is broken and why, and tell me what to change.',
    'Ask for anything missing rather than guessing at it.',
    ''
  ]
}

function healthSection(environments: EnvironmentHealth[]): string[] {
  if (!environments.length) {
    return ['## Environments', '', 'No environment URLs are declared for this repository, so nothing was probed.', '']
  }
  const failing = environments.filter(isFailing)
  const out = [
    '## Environments',
    '',
    failing.length
      ? `${failing.length} of ${environments.length} failing.`
      : `All ${environments.length} reachable and returning 2xx.`,
    ''
  ]
  for (const e of environments) out.push(...envLines(e))
  out.push('')
  return out
}

function repoContext(state: RepoState): string[] {
  const out = [
    '## Repository',
    '',
    `- repo: ${state.repo}${state.private ? ' (private)' : ''}`,
    `- url: ${state.url}`,
    `- default branch: \`${state.defaultBranch}\``,
    `- environment mapping: ${
      Object.entries(state.envMapping).map(([env, branch]) => `${env} = \`${branch}\``).join(', ') || 'none detected'
    }`,
    `- mapping source: ${state.hasOverride ? '`.github/branch-viz.json`' : 'auto-detected from branch names'}`,
    ''
  ]

  const envBranches = state.branches.filter(b => b.env)
  if (envBranches.length) {
    out.push('### CI on the environment branches', '')
    for (const b of envBranches) {
      const head = b.commits[0]
      out.push(
        `- \`${b.name}\` (${b.env}): checks **${b.checks ?? 'none reported'}**${b.protected ? ', protected' : ''}`,
        head ? `  - head: ${head.sha.slice(0, 7)} "${head.message.split('\n')[0]}" by ${head.author.alias}, ${head.committedAt}` : '  - no commits'
      )
    }
    out.push('')
  }

  if (state.promotions.length) {
    out.push('### Promotion state', '')
    for (const p of state.promotions) {
      out.push(
        `- \`${p.sourceBranch}\` -> \`${p.targetBranch}\` (${p.from} -> ${p.to}): **${p.status}**` +
          `${p.ahead !== null ? `, ${p.ahead} waiting to promote` : ''}` +
          `${p.behind ? `, target is ${p.behind} ahead` : ''}`
      )
    }
    out.push('')
  }

  const stuckPrs = state.pullRequests.filter(pr => pr.checks === 'failure' || pr.mergeable === 'CONFLICTING')
  if (stuckPrs.length) {
    out.push('### Open pull requests that cannot merge as-is', '')
    for (const pr of stuckPrs) {
      out.push(
        `- #${pr.number} "${pr.title}" (\`${pr.head}\` -> \`${pr.base}\`): checks ${pr.checks ?? 'none'}, ${pr.mergeable}, review ${pr.reviewDecision ?? 'none'}`
      )
    }
    out.push('')
  }

  return out
}

/** The repo-level report: one repository, its environments, its git state. */
export function repoDiagnostics(state: RepoState, health: RepoHealth | null): string {
  const lines = [
    ...preamble(state.repo), // already owner-qualified
    ...healthSection(health?.environments ?? []),
    ...repoContext(state),
    '---',
    `Repository synced ${state.fetchedAt}. ${
      health ? `Environments probed ${health.checkedAt} in ${health.tookMs}ms.` : 'Environments were not probed.'
    }`
  ]
  return lines.join('\n')
}

function databaseLines(s: AzureServerSummary): string[] {
  const out = [
    `- **${s.name}** (${s.repo ?? 'no owning repo tagged'}${s.env ? `, ${s.env}` : ''}${s.role ? `, ${s.role}` : ''})`,
    `  - ${s.host ?? 'no hostname'}: ${s.engine} ${s.version ?? '?'}, ${s.tier ?? '?'} ${s.sku ?? ''}, ${s.location ?? '?'}`,
    `  - provisioning state: \`${s.state}\` (${s.statusLabel})`,
    `  - ${s.publicNetworkAccess === null ? 'network posture unknown' : s.publicNetworkAccess ? 'public network access enabled' : 'public network access disabled'}` +
      `, ${s.highAvailability ? 'HA on' : 'no HA'}` +
      `, ${s.backupRetentionDays ? `${s.backupRetentionDays}-day backups` : 'backup retention unknown'}` +
      `, ${s.entraAuth ? 'Entra auth enabled' : 'password auth only'}`
  ]
  return out
}

function tableStoreLines(t: AzureTableStoreSummary): string[] {
  const m = t.metrics
  return [
    `- **${t.name}** (${t.repo ?? 'no owning repo tagged'}${t.env ? `, ${t.env}` : ''}${t.dataClass ? `, ${t.dataClass}` : ''})`,
    `  - ${t.purpose ?? 'no purpose tag'}; tables: ${t.tables.join(', ') || 'none'}`,
    m
      ? `  - account totals (Azure's hourly rollup, not per table): ${m.entities ?? '?'} entities, ${
          m.capacityBytes ?? '?'
        } bytes, ${m.transactions24h ?? '?'} operations in 24h`
      : '  - no metrics reported for this account',
    `  - ${t.host ?? 'no table endpoint'}: ${t.sku ?? '?'}, ${t.location ?? '?'}, provisioning state \`${t.state}\` (${t.statusLabel})`
  ]
}

function sqlDatabaseLines(d: AzureSqlDatabaseSummary): string[] {
  return [
    `- **${d.name}** on ${d.server ?? '?'} (${d.repo ?? 'no owning repo tagged'}${d.env ? `, ${d.env}` : ''}${d.role ? `, ${d.role}` : ''})`,
    `  - ${d.host ?? 'no hostname'}: ${d.engine} ${d.version ?? '?'}, ${d.tier ?? '?'} ${d.sku ?? ''}, ${d.location ?? '?'}`,
    `  - status \`${d.state}\` (${d.statusLabel})` +
      `${d.autoPauseMinutes ? `, serverless, auto-pauses after ${d.autoPauseMinutes} min` : ''}` +
      `, ${d.entraOnlyAuth ? 'Entra-only auth' : 'SQL auth allowed'}`
  ]
}

/**
 * The project-level report: every declared environment across the org, plus
 * the database inventory. This is the one to paste when the question is
 * "what is broken anywhere", not "why is this repo broken".
 */
export function orgDiagnostics(health: OrgHealth, databases: DatabasesResponse | null): string {
  const all = health.repos.flatMap(r => r.environments.map(e => ({ repo: r.repo, e })))
  const failing = all.filter(x => isFailing(x.e))

  const lines = [
    ...preamble(`${health.org}, all environments`),
    '## Summary',
    '',
    `- repositories scanned: ${health.scanned}${health.truncated ? ' (list truncated, more exist)' : ''}`,
    `- repositories declaring environment URLs: ${health.repos.filter(r => r.declared > 0).length}`,
    `- environments probed: ${all.length}`,
    `- **failing: ${failing.length}**`,
    ''
  ]

  if (failing.length) {
    lines.push('## Failing environments', '')
    for (const { repo, e } of failing) {
      lines.push(`### ${repo} / ${e.env}`, '')
      lines.push(...envLines(e))
      lines.push('')
    }
  }

  lines.push('## All environments by repository', '')
  for (const r of health.repos) {
    lines.push(`### ${r.repo}`, '')
    if (r.error) {
      lines.push(`Could not read this repository's config: ${r.error}`, '')
      continue
    }
    for (const e of r.environments) lines.push(...envLines(e))
    lines.push('')
  }

  const azure = databases?.azure
  if (azure && !azure.configured) {
    lines.push('## Databases', '', 'The Azure panel is not configured on this deployment, so no database state was read.', '')
  } else if (azure) {
    const servers = azure.servers ?? []
    const stores = azure.tableStores ?? []
    const sql = azure.sqlDatabases ?? []
    if (servers.length || stores.length || sql.length) {
      lines.push(
        '## Databases (Azure)',
        '',
        'Read from Azure Resource Manager with a Reader-only credential. These are **not**',
        'connection-tested: reaching a database depends on its firewall rules, and this',
        'dashboard is on no allow list, so a connection attempt from here would measure the',
        'firewall rather than the database. State below is what ARM reports. Table Storage',
        'shows table names only; no row is ever read.',
        ''
      )
      if (servers.length) {
        lines.push('### PostgreSQL flexible servers', '')
        for (const s of servers) lines.push(...databaseLines(s))
        lines.push('')
      }
      if (stores.length) {
        lines.push('### Table Storage', '')
        for (const t of stores) lines.push(...tableStoreLines(t))
        lines.push('')
      }
      if (sql.length) {
        lines.push('### Azure SQL', '')
        for (const d of sql) lines.push(...sqlDatabaseLines(d))
        lines.push('')
      }
    }
    if (azure.error) {
      if (!(servers.length || stores.length || sql.length)) lines.push('## Databases', '')
      lines.push(`Azure returned an error: ${azure.error}`, '')
    }
  }

  lines.push('---', `Probed ${health.checkedAt} in ${health.tookMs}ms.`)
  return lines.join('\n')
}

/** Clipboard with a fallback, since the API needs a secure context and permission. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }
}
